import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { inboxChanged } from "@/lib/inbox";
import { api } from "@/lib/api";
import { uploadMediaFile, describeUploadError } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Lock, Globe, Pencil, Trash2, Check, X, ChevronLeft, Images, Share2, Upload, Star, Heart, Image as ImageIcon, Sparkles, CheckSquare, Square, Tag, MoreHorizontal, Copy, MapPin } from "lucide-react";
import { BulkLocationDialog } from "@/components/BulkLocationDialog";
import { TagInput } from "@/components/TagInput";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Admin-only ••• menu for rule-based (smart) galleries: Edit / Duplicate / Delete the DEFINITION only — never media.
const SmartGalleryAdminMenu = ({ data, cid, navigate }) => {
  const [confirmDel, setConfirmDel] = useState(false);
  const duplicate = async () => {
    try { const r = await api.post(`/collections/tag-query/${cid}/duplicate`); toast.success(`Created "${r.data.name}"`); navigate(`/collection/${r.data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
  };
  const remove = async () => {
    try { await api.delete(`/collections/${cid}`); toast.success(`Deleted smart gallery "${data.name}" — no photos or videos were touched`); navigate("/admin?tab=galleries&sub=smart"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setConfirmDel(false);
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="smart-admin-menu" title="Smart gallery options (admin)" className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-border hover:border-primary/50"><MoreHorizontal className="w-4 h-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Smart gallery · admin</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="smart-admin-edit" className="cursor-pointer" onClick={() => navigate(`/admin?tab=galleries&sub=smart&edit=${cid}`)}><Pencil className="w-3.5 h-3.5 mr-2" /> Edit Smart Gallery</DropdownMenuItem>
          <DropdownMenuItem data-testid="smart-admin-duplicate" className="cursor-pointer" onClick={duplicate}><Copy className="w-3.5 h-3.5 mr-2" /> Duplicate</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="smart-admin-delete" className="cursor-pointer text-red-400 focus:text-red-300" onClick={() => setConfirmDel(true)}><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Smart Gallery</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="smart-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{data.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the virtual gallery definition only. It does NOT delete any photos or videos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="smart-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="smart-delete-confirm" onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete Gallery</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { window.URL.revokeObjectURL(v.src); resolve(v.duration || 0); };
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });

const Collection = ({ cidOverride = null, inboxMode = false }) => {
  const { cid: routeCid } = useParams();
  const cid = cidOverride || routeCid;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get("type");
  const { canUpload, user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  const pinnedRef = useRef(null);
  const pinnedIdxRef = useRef(0);
  useEffect(() => { if (openId) pinnedRef.current = (data?.items || []).find((x) => x.id === openId) || pinnedRef.current; else pinnedRef.current = null; }, [openId, data]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState({ active: false, done: 0, total: 0 });
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [bulkTags, setBulkTags] = useState([]);
  const [bulkMode, setBulkMode] = useState("add"); // add | remove
  const [applying, setApplying] = useState(false);
  const isAdmin = !!user?.is_admin;
  // Context-aware bulk actions: No Location -> Set Location first; Inbox/tag galleries -> tagging first.
  const bulkContext = data?.system_key === "no-location" ? "location" : data?.system_key === "inbox" ? "review" : "tags";
  const [bulkAction, setBulkActionState] = useState(null); // tags | location (null = context default)
  const activeBulk = bulkAction || (bulkContext === "location" ? "location" : "tags");
  const [locOpen, setLocOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashing, setTrashing] = useState(false);
  const [trashFailed, setTrashFailed] = useState([]);
  // Bulk "Move N to Trash": ONE batch request through the same canonical Trash helper as single Delete Post.
  const bulkTrash = async () => {
    const ids = [...sel];
    setTrashing(true);
    try {
      const r = await api.post("/admin/media/bulk-delete", { ids });
      const { moved = 0, failed = [] } = r.data || {};
      setTrashOpen(false);
      setTrashFailed(failed);
      if (failed.length) toast.warning(`${moved} moved to Trash · ${failed.length} failed`, { description: failed.map((f) => `${f.title || f.id}: ${f.reason}`).join(" · "), duration: 9000 });
      else toast.success(`${moved} post${moved === 1 ? "" : "s"} moved to Trash`, { description: "Restore any time from Admin → Trash." });
      const failedIds = new Set(failed.map((f) => f.id));
      setSel(new Set(ids.filter((id) => failedIds.has(id))));
      if (!failedIds.size) exitSelect();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't move to Trash");
    } finally { setTrashing(false); }
  };
  const selectLabel = bulkContext === "location" ? "Select & set location" : bulkContext === "review" ? "Select & review" : "Select & bulk-tag";

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSel(new Set((data?.items || []).map((x) => x.id)));
  const exitSelect = () => { setSelectMode(false); setSel(new Set()); setBulkTags([]); setBulkMode("add"); setBulkActionState(null); };
  const onBulkLocationDone = (r) => {
    // rule-based gallery re-evaluates on reload: located items drop out of No Location and the count updates
    const touched = new Set(sel);
    exitSelect();
    load();
    if (bulkContext === "location" && r?.updated) toast.message(`${r.updated} item${r.updated === 1 ? "" : "s"} left No Location`, { description: "They now show under their place on the map and in city galleries." });
    return touched;
  };
  const applyBulk = async () => {
    if (!sel.size || !bulkTags.length) return;
    setApplying(true);
    try {
      if (bulkMode === "remove") {
        await api.post("/admin/media/bulk_tags", { ids: [...sel], remove: bulkTags });
        toast.success(`Removed ${bulkTags.length} tag${bulkTags.length === 1 ? "" : "s"} from ${sel.size} photo${sel.size === 1 ? "" : "s"}`);
      } else {
        await api.post("/admin/media/bulk_tags", { ids: [...sel], add: bulkTags, approve: true });
        toast.success(`Added ${bulkTags.length} tag${bulkTags.length === 1 ? "" : "s"} to ${sel.size} photo${sel.size === 1 ? "" : "s"} — cleared from Inbox`);
      }
      exitSelect();
      load();
    } catch { toast.error("Couldn't apply tags"); }
    setApplying(false);
  };

  const addUploaded = async (ids) => {
    if (!ids || !ids.length) { load(); return; }
    try {
      const existing = (data?.items || []).map((x) => x.id);
      await api.patch(`/collections/${cid}`, { media_ids: [...ids, ...existing] });
    } catch { toast.error("Uploaded, but couldn't add to gallery"); }
    load();
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image") || f.type.startsWith("video"));
    if (!files.length) return;
    const isAdmin = !!user?.is_admin;
    setUploading({ active: true, done: 0, total: files.length });
    const created = [];
    for (const f of files) {
      try {
        let duration = 0;
        if (f.type.startsWith("video")) {
          duration = await getVideoDuration(f);
          if (!isAdmin && duration > 31) {
            toast.error(`"${f.name}" is ${Math.round(duration)}s — videos must be 30s or less`);
            setUploading((u) => ({ ...u, done: u.done + 1 }));
            continue;
          }
        }
        const m = await uploadMediaFile(f, { title: f.name.replace(/\.[^.]+$/, ""), description: "", duration: String(duration), location_privacy: "city" });
        if (m?.id) created.push(m.id);
      } catch (e) {
        toast.error(`"${f.name}" — ${describeUploadError(e)}`, { duration: 12000 });
      }
      setUploading((u) => ({ ...u, done: u.done + 1 }));
    }
    setUploading({ active: false, done: 0, total: 0 });
    if (created.length) {
      toast.success(user?.is_admin ? `Added ${created.length} to this gallery` : `Uploaded ${created.length} — pending review, added here`);
      await addUploaded(created);
    }
  };

  const load = useCallback(async () => {
    try { const r = await api.get(`/collections/${cid}`); setData(r.data); setName(r.data.name); if (inboxMode) inboxChanged(); }
    catch (e) { setErr(e?.response?.status === 403 ? "This gallery is private." : "Gallery not found."); }
  }, [cid, inboxMode]);
  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (!name.trim()) return;
    setEditing(false);
    try { await api.patch(`/collections/${cid}`, { name }); setData((d) => ({ ...d, name })); }
    catch { toast.error("Failed"); }
  };
  const toggleVis = async () => {
    const v = data.visibility === "public" ? "private" : "public";
    setData((d) => ({ ...d, visibility: v }));
    try { await api.patch(`/collections/${cid}`, { visibility: v }); toast.success(v === "private" ? "Now private" : "Now public"); }
    catch { toast.error("Failed"); }
  };
  const removeItem = async (id) => {
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id), count: d.count - 1 }));
    try { await api.post(`/collections/${cid}/toggle`, { media_id: id }); } catch { /* ignore */ }
  };
  const setCover = async (id, e) => {
    e?.stopPropagation();
    setData((d) => ({ ...d, cover_media_id: id }));
    try { await api.patch(`/collections/${cid}`, { cover_media_id: id }); toast.success("Cover updated"); } catch { toast.error("Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this gallery? The photos themselves are not deleted.")) return;
    try { await api.delete(`/collections/${cid}`); toast.success("Gallery deleted"); navigate("/profile"); } catch { toast.error("Failed"); }
  };
  const share = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere!"); }
    catch { window.prompt("Copy this link:", url); }
    if (data?.visibility === "private") toast("This gallery is private — switch it to Public so others can open the link.", { icon: "🔒" });
  };
  const toggleFeature = async () => {
    try { const r = await api.post(`/collections/${cid}/feature`); setData((d) => ({ ...d, is_featured: r.data.featured })); toast.success(r.data.featured ? "Pinned to your profile" : "Unpinned from profile"); }
    catch { toast.error("Failed"); }
  };
  const toggleLike = async () => {
    try { const r = await api.post(`/collections/${cid}/like`); setData((d) => ({ ...d, liked: r.data.liked, likes: r.data.likes })); }
    catch { toast.error("Sign in to like"); }
  };

  if (err) return (
    <div className="min-h-screen bg-background"><Navbar />
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Lock className="w-9 h-9 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">{err}</p>
        <button onClick={() => navigate("/browse")} className="mt-4 text-primary text-sm">Back to browse</button>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  const reasonFilter = inboxMode ? searchParams.get("reason") : null;   // title | description | location (existing inbox_reasons, no new queue)
  const REASON_RE = { title: /title/i, description: /description/i, tags: /tags/i, location: /location/i };
  const hasReason = (x, kind) => (x.inbox_reasons || []).some((r) => (REASON_RE[kind] || /$^/).test(r));
  const visItems = ((typeFilter === "image" || typeFilter === "video")
    ? (data.items || []).filter((x) => x.media_type === typeFilter)
    : (data.items || [])).filter((x) => !reasonFilter || hasReason(x, reasonFilter));
  // An item being edited must never vanish from the queue mid-edit (e.g. after a background write): pin it until closed —
  // at its last known position, so "next" still means the item that followed it (never the top of the list).
  if (inboxMode && openId && !visItems.some((x) => x.id === openId) && pinnedRef.current?.id === openId) visItems.splice(Math.min(pinnedIdxRef.current, visItems.length), 0, pinnedRef.current);
  else if (inboxMode && openId) { const i = visItems.findIndex((x) => x.id === openId); if (i >= 0) pinnedIdxRef.current = i; }
  // `all` counts unique MEDIA items (one item with three reasons counts once); per-reason counters count items carrying that reason
  const inboxCounts = inboxMode ? { all: (data.items || []).length, title: (data.items || []).filter((x) => hasReason(x, "title")).length, description: (data.items || []).filter((x) => hasReason(x, "description")).length, tags: (data.items || []).filter((x) => hasReason(x, "tags")).length, location: (data.items || []).filter((x) => hasReason(x, "location")).length } : null;
  const setReason = (r) => { const p = new URLSearchParams(searchParams); if (r) p.set("reason", r); else p.delete("reason"); setSearchParams(p); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8">
        {!inboxMode && <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="w-4 h-4" /> Back</button>}
        {inboxMode ? (
          <div data-testid="inbox-header" className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="font-heading text-3xl" data-testid="inbox-title">Inbox <span className="text-muted-foreground">—</span> <span data-testid="inbox-count">{reasonFilter ? `${visItems.length} of ${inboxCounts.all}` : inboxCounts.all}</span> {inboxCounts.all === 1 && !reasonFilter ? "item" : "items"}</h1>
              <p className="text-sm text-muted-foreground mt-1" data-testid="inbox-subtitle">Needs a title, description, tags or location · open an item → review → Save &amp; Next</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="inbox-filters">
                {[["", "All", inboxCounts.all], ["title", "Needs Title", inboxCounts.title], ["description", "Needs Description", inboxCounts.description], ["tags", "Needs Tags", inboxCounts.tags], ["location", "Needs Location", inboxCounts.location]].map(([v, l, n]) => (
                  <button key={v || "all"} type="button" data-testid={`inbox-filter-${v || "all"}`} aria-pressed={(reasonFilter || "") === v} onClick={() => setReason(v)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${(reasonFilter || "") === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"}`}>
                    {l} <span className="text-xs opacity-80 tabular-nums">{n}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="inbox-go-dashboard" onClick={() => navigate("/profile")} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 hover:border-primary/50">Dashboard</button>
              <button data-testid="inbox-add-content" onClick={() => navigate("/admin?tab=upload")} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-medium hover:bg-primary/90"><Upload className="w-3.5 h-3.5" /> Add Content</button>
            </div>
          </div>
        ) : (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <input autoFocus data-testid="collection-edit-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()}
                  className="bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-2xl font-heading outline-none focus:border-primary" />
                <button data-testid="collection-name-save" onClick={saveName} className="text-primary"><Check className="w-5 h-5" /></button>
                <button onClick={() => { setEditing(false); setName(data.name); }} className="text-muted-foreground"><X className="w-5 h-5" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-3xl truncate">{data.name}</h1>
                {data.is_owner && <button data-testid="collection-edit-btn" onClick={() => setEditing(true)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-1" data-testid="collection-count">
              {typeFilter === "image" || typeFilter === "video"
                ? <>{visItems.length} {typeFilter === "video" ? (visItems.length === 1 ? "video" : "videos") : (visItems.length === 1 ? "photo" : "photos")} <span className="opacity-70">· of {data.count} items</span> <button type="button" data-testid="collection-clear-type" onClick={() => { const p = new URLSearchParams(searchParams); p.delete("type"); setSearchParams(p, { replace: true }); }} className="underline hover:text-foreground ml-1">show all</button></>
                : <>{data.count} items</>} · by @{data.owner_name}
            </p>
            {data.is_smart && (
              <div data-testid="smart-gallery-badge" className="mt-2 inline-flex items-center gap-1.5 text-xs rounded-full bg-primary/10 border border-primary/30 text-primary px-3 py-1">
                <Sparkles className="w-3.5 h-3.5" /> Smart gallery · auto-updates
                {data.media_type_filter && data.media_type_filter !== "all" ? ` · ${data.media_type_filter === "video" ? "Videos" : "Photos"} only` : ""}
                {data.library_filter && data.library_filter !== "all" ? ` · ${data.library_filter}` : ""}
                {data.query_text ? ` · "${data.query_text}"` : ""}
              </div>
            )}
            <button data-testid="collection-like" onClick={toggleLike}
              className={`mt-2 inline-flex items-center gap-1.5 text-sm rounded-full border px-3 py-1 transition-colors ${data.liked ? "border-rose-500/50 text-rose-400 bg-rose-500/10" : "border-border hover:border-rose-500/40"}`}>
              <Heart className={`w-3.5 h-3.5 ${data.liked ? "fill-rose-400" : ""}`} /> {data.likes || 0}
            </button>
          </div>
          {data.is_owner ? (
            <div className="flex items-center gap-2">
              {canUpload && !data.is_smart && <button data-testid="collection-upload-here" onClick={() => fileRef.current?.click()} disabled={uploading.active} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-medium hover:bg-primary/90 disabled:opacity-70">{uploading.active ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {uploading.done}/{uploading.total}…</> : <><Upload className="w-3.5 h-3.5" /> Upload here</>}</button>}
              <button data-testid="collection-feature" onClick={toggleFeature} className={`inline-flex items-center gap-1.5 text-sm rounded-full border px-3 py-1.5 transition-colors ${data.is_featured ? "border-primary text-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                <Star className={`w-3.5 h-3.5 ${data.is_featured ? "fill-primary" : ""}`} /> {data.is_featured ? "Pinned" : "Pin to profile"}
              </button>
              <button data-testid="collection-share" onClick={share} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 hover:border-primary/50"><Share2 className="w-3.5 h-3.5" /> Share</button>
              <button data-testid="collection-vis-toggle" onClick={toggleVis}
                className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 hover:border-primary/50">
                {data.visibility === "private" ? <><Lock className="w-3.5 h-3.5" /> Private</> : <><Globe className="w-3.5 h-3.5" /> Public</>}
              </button>
              {isAdmin && data.is_smart && !data.is_system
                ? <SmartGalleryAdminMenu data={data} cid={cid} navigate={navigate} />
                : <button data-testid="collection-delete" onClick={del} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-destructive/40 text-red-400 px-3 py-1.5 hover:bg-destructive/10"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
            </div>
          ) : (
            <button data-testid="collection-share" onClick={share} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 hover:border-primary/50"><Share2 className="w-3.5 h-3.5" /> Share</button>
          )}
        </div>
        )}

        <div className="mt-8">
          {isAdmin && data.items.length > 0 && (
            <div className="flex items-center gap-2 mb-3" data-testid="bulk-tag-toolbar">
              {!selectMode ? (
                <button data-testid="bulk-select-toggle" onClick={() => setSelectMode(true)}
                  className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 hover:border-primary/50">
                  {bulkContext === "location" ? <MapPin className="w-3.5 h-3.5" aria-hidden="true" /> : <CheckSquare className="w-3.5 h-3.5" aria-hidden="true" />} {selectLabel}
                </button>
              ) : (
                <>
                  <span className="text-sm text-muted-foreground">{sel.size} selected</span>
                  <button data-testid="bulk-select-all" onClick={selectAll} className="text-sm text-primary hover:underline">Select all</button>
                  <button data-testid="bulk-select-cancel" onClick={exitSelect} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                </>
              )}
            </div>
          )}
          {visItems.length === 0 && inboxMode ? (
            <div data-testid="inbox-clear" className="border border-dashed border-border rounded-xl py-16 text-center">
              <Check className="w-9 h-9 mx-auto mb-3 text-primary" />
              <p className="font-heading text-2xl">{reasonFilter ? "Nothing left in this filter" : "Inbox clear"}</p>
              <p className="text-sm text-muted-foreground mt-1">{reasonFilter ? `${inboxCounts.all} item${inboxCounts.all === 1 ? "" : "s"} remain under other reasons.` : "Every post has a title, description and (where eligible) a location."}</p>
              <div className="mt-5 flex items-center justify-center gap-2">
                {reasonFilter && <button data-testid="inbox-clear-show-all" onClick={() => setReason("")} className="rounded-full border border-border px-4 py-2 text-sm hover:border-primary/50">Show all</button>}
                <button data-testid="inbox-clear-dashboard" onClick={() => navigate("/profile")} className="rounded-full border border-border px-4 py-2 text-sm hover:border-primary/50">Dashboard</button>
                <button data-testid="inbox-clear-add-content" onClick={() => navigate("/admin?tab=upload")} className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90">Add Content</button>
              </div>
            </div>
          ) : visItems.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl py-16 text-center text-muted-foreground">
              <Images className="w-8 h-8 mx-auto mb-3" />
              {typeFilter ? `No ${typeFilter === "video" ? "videos" : "photos"} in here.`
                : data.is_smart ? "No matches yet — new photos that fit this search will appear here automatically, and you'll get a notification."
                : data.is_owner ? (canUpload ? "Empty — tap “Upload here” to add new photos, or open any photo and bookmark it to save it here." : "Empty — open any photo and tap the bookmark to save it into this gallery.") : "This gallery is empty."}
            </div>
          ) : (
            <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 ${selectMode ? "pb-28" : ""}`} data-testid="collection-items">
              {visItems.map((item, i) => {
                const picked = sel.has(item.id);
                return (
                <div key={item.id} className="relative group">
                  <MediaTile item={item} index={i} onOpen={(x) => selectMode ? toggleSel(x.id) : setOpenId(x.id)} />
                  {isAdmin && data?.system_key === "inbox" && Array.isArray(item.inbox_reasons) && item.inbox_reasons.length > 0 && (
                    <div data-testid={`inbox-reason-${item.id}`} className="mt-1.5 flex flex-wrap gap-1">
                      {item.inbox_reasons.map((r) => (
                        <span key={r} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30">{r}</span>
                      ))}
                    </div>
                  )}
                  {selectMode && (
                    <button data-testid={`bulk-pick-${item.id}`} onClick={() => toggleSel(item.id)}
                      className={`absolute inset-0 z-20 rounded-xl transition-colors ${picked ? "ring-2 ring-primary bg-primary/15" : "hover:bg-black/20"}`}>
                      <span className="absolute top-2 left-2 text-white drop-shadow">
                        {picked ? <CheckSquare className="w-5 h-5 text-primary fill-primary/20" /> : <Square className="w-5 h-5" />}
                      </span>
                    </button>
                  )}
                  {data.cover_media_id === item.id && (
                    <span className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Cover</span>
                  )}
                  {data.is_owner && !data.is_smart && !selectMode && (
                    <>
                      {data.cover_media_id !== item.id && (
                        <button data-testid={`collection-setcover-${item.id}`} onClick={(e) => setCover(item.id, e)}
                          className="absolute bottom-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity text-[11px] font-medium px-2 py-1 rounded-full bg-black/75 backdrop-blur text-white hover:bg-primary hover:text-primary-foreground">Set cover</button>
                      )}
                      <button data-testid={`collection-remove-${item.id}`} onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-white hover:bg-destructive transition-colors"><X className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-t border-border p-3" data-testid="bulk-tag-bar">
          <div className="max-w-[1600px] mx-auto flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium inline-flex items-center gap-1.5 shrink-0" data-testid="bulk-selected-count">
              {activeBulk === "location" ? <MapPin className="w-4 h-4 text-primary" aria-hidden="true" /> : <Tag className="w-4 h-4 text-primary" aria-hidden="true" />} {sel.size} selected
            </span>
            {activeBulk === "location" ? (
              <>
                <Button data-testid="bulk-set-location" onClick={() => setLocOpen(true)} disabled={!sel.size} className="rounded-full shrink-0">
                  <MapPin className="w-4 h-4 mr-1.5" aria-hidden="true" /> Set Location{sel.size ? ` for ${sel.size}` : ""}
                </Button>
                <span className="text-xs text-muted-foreground flex-1 min-w-[200px]" data-testid="bulk-location-hint">
                  {bulkContext === "location" ? "Items leave No Location automatically once a place is saved." : "Applies one place to every selected item."}
                </span>
              </>
            ) : (
              <>
                <div className="inline-flex rounded-full border border-border overflow-hidden shrink-0" data-testid="bulk-mode-toggle">
                  <button data-testid="bulk-mode-add" onClick={() => setBulkMode("add")}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${bulkMode === "add" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Add tags</button>
                  <button data-testid="bulk-mode-remove" onClick={() => setBulkMode("remove")}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${bulkMode === "remove" ? "bg-red-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>Remove tags</button>
                </div>
                <div className="flex-1 min-w-[220px]"><TagInput value={bulkTags} onChange={setBulkTags} enableAi={false} placeholder={bulkMode === "remove" ? "type tags to remove from all…" : bulkContext === "review" ? "type tags — saving also clears these from the Inbox…" : "type tags to apply to all…"} testid="bulk-tag-input" /></div>
                <Button data-testid="bulk-tag-apply" onClick={applyBulk} disabled={applying || !sel.size || !bulkTags.length} className={`rounded-full shrink-0 ${bulkMode === "remove" ? "bg-red-500 hover:bg-red-600 text-white" : ""}`}>
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : `${bulkMode === "remove" ? "Remove from" : bulkContext === "review" ? "Save & approve" : "Apply to"} ${sel.size}`}
                </Button>
              </>
            )}
            {isAdmin && (
              <Button data-testid="bulk-trash-btn" variant="outline" onClick={() => setTrashOpen(true)} disabled={!sel.size || trashing}
                className="rounded-full shrink-0 border-destructive/50 text-red-400 hover:bg-destructive/10 hover:text-red-300">
                {trashing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Trash2 className="w-4 h-4 mr-1.5" aria-hidden="true" />} Move {sel.size} to Trash
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="bulk-actions-menu" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary/50 shrink-0"><MoreHorizontal className="w-3.5 h-3.5" aria-hidden="true" /> Bulk actions</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">For {sel.size} selected</DropdownMenuLabel>
                <DropdownMenuItem data-testid="bulk-action-location" onClick={() => setBulkActionState("location")} className={activeBulk === "location" ? "text-primary" : ""}><MapPin className="w-3.5 h-3.5 mr-2" aria-hidden="true" /> Set Location</DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-action-tags" onClick={() => setBulkActionState("tags")} className={activeBulk === "tags" ? "text-primary" : ""}><Tag className="w-3.5 h-3.5 mr-2" aria-hidden="true" /> Add / remove tags</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
      <BulkLocationDialog open={locOpen} onOpenChange={setLocOpen} ids={[...sel]} onDone={onBulkLocationDone} />
      <AlertDialog open={trashOpen} onOpenChange={setTrashOpen}>
        <AlertDialogContent data-testid="bulk-trash-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move {sel.size} selected post{sel.size === 1 ? "" : "s"} to Trash?</AlertDialogTitle>
            <AlertDialogDescription>They will be removed from normal galleries and search and can be restored from Trash. Files, tags, ratings and comments are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-trash-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-trash-confirm" onClick={(e) => { e.preventDefault(); bulkTrash(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {trashing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : `Move ${sel.size} to Trash`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {trashFailed.length > 0 && selectMode && (
        <div data-testid="bulk-trash-failed" className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 rounded-lg border border-amber-400/50 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
          {trashFailed.length} couldn't be moved: {trashFailed.map((f) => `${f.title || f.id} (${f.reason})`).join(", ")}
        </div>
      )}
      <MediaLightbox mediaId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} onNavigate={setOpenId} playlist={visItems} galleryContext={data ? { id: data.id, name: data.name, isSmart: !!data.is_smart, isSystem: !!data.is_system, systemKey: data.system_key, inboxQueue: inboxMode, ownerId: data.owner_id, editHref: data.is_smart && !data.is_system ? `/admin?tab=galleries&sub=smart&edit=${data.id}` : null } : null} />
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="collection-file-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

export default Collection;
